import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"

// Simple native select wrapper
const Select = ({ value, onValueChange, children, ...props }) => {
  const [options, setOptions] = React.useState([]);
  const [displayValue, setDisplayValue] = React.useState('');

  React.useEffect(() => {
    // Extract options from SelectContent > SelectItem children
    const extractedOptions = [];
    React.Children.forEach(children, (child) => {
      if (child?.type?.displayName === 'SelectContent') {
        React.Children.forEach(child.props.children, (item) => {
          if (item?.type?.displayName === 'SelectItem') {
            extractedOptions.push({
              value: item.props.value,
              label: item.props.children
            });
          }
        });
      }
    });
    setOptions(extractedOptions);
    
    // Update display value
    const selected = extractedOptions.find(opt => opt.value === value);
    setDisplayValue(selected ? selected.label : '');
  }, [children, value]);

  return (
    <div className="relative w-full" {...props}>
      <select
        value={value || ''}
        onChange={(e) => onValueChange?.(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      >
        <option value="">Select...</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {React.Children.map(children, child => {
        if (child?.type?.displayName === 'SelectTrigger') {
          return React.cloneElement(child, { displayValue });
        }
        return null;
      })}
    </div>
  );
};

const SelectTrigger = React.forwardRef(({ className, children, displayValue, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer hover:border-gray-400",
        className
      )}
      {...props}
    >
      <span className="block truncate">{displayValue || 'Select...'}</span>
      <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
    </div>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

const SelectValue = ({ placeholder }) => {
  return null; // Value display is handled by SelectTrigger
};

const SelectContent = ({ children }) => {
  return null; // Content is handled by Select component
};
SelectContent.displayName = 'SelectContent';

const SelectItem = ({ value, children }) => {
  return null; // Items are extracted by Select component
};
SelectItem.displayName = 'SelectItem';

const SelectLabel = ({ children, ...props }) => {
  return null;
};

const SelectGroup = ({ children }) => {
  return <>{children}</>;
};

const SelectSeparator = () => {
  return null;
};

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
